/**
 * The signed-in person's own corner (spec §5 Profil, the avatar menu) and §11.3's team list.
 *
 * Every route answers for the caller's own membership, except `team/status/`, which a branch
 * manager reads for their branch and the owner for the agency.
 */
import { apiRequest } from "@/lib/api";

export type ManualStatus = "working" | "break" | "off";
export type EffectiveStatus = "working" | "break" | "off";
export type Theme = "light" | "dark" | "system";
export type InterfaceLanguage = "uz" | "ru";
export type CurrencyDisplay = "symbol" | "code";

export interface QuietHours {
  enabled: boolean;
  from: string;
  to: string;
}

export interface Preferences {
  theme: Theme;
  language: InterfaceLanguage;
  currency_display: CurrencyDisplay;
  message_signature: string;
  quiet_hours: QuietHours;
}

export interface ProfileUpdate {
  full_name?: string;
  phone?: string;
  email?: string;
  display_name?: string;
}

export interface LeaveBadge {
  id: number;
  kind: "leave" | "sick";
  starts_on: string;
  ends_on: string;
  handover_to: string;
  active: boolean;
}

export interface Availability {
  manual_status: ManualStatus;
  /** Monday = 0 … Sunday = 6. Empty means no weekly schedule. */
  work_days: number[];
  starts_at: string | null;
  ends_at: string | null;
  auto_offline: boolean;
  effective_status: EffectiveStatus;
  leave: LeaveBadge | null;
}

export type AvailabilityUpdate = Partial<
  Pick<Availability, "manual_status" | "work_days" | "starts_at" | "ends_at" | "auto_offline">
>;

export interface LeaveRow {
  id: number;
  kind: "leave" | "sick";
  starts_on: string;
  ends_on: string;
  handover_to: { id: number; name: string };
  note: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  started: boolean;
  ended: boolean;
  decision_note: string;
}

export interface LeaveCreate {
  kind: "leave" | "sick";
  starts_on: string;
  ends_on: string;
  handover_to: number;
  note: string;
}

export interface Colleague {
  /** The membership row. */
  id: number;
  /** The person: what a hand-over or an assignment names. */
  user: number;
  name: string;
  role: string;
  branch: string;
}

export interface TeamMember {
  id: number;
  /** The user behind the membership: what an owner picker filters and reassigns by. */
  user: number;
  name: string;
  email: string;
  role: string;
  branch: string;
  effective_status: EffectiveStatus;
  work_days: number[];
  starts_at: string | null;
  ends_at: string | null;
  leave: LeaveBadge | null;
}

export interface DeviceSession {
  id: number;
  user_agent: string;
  created_at: string;
  last_seen_at: string;
  current: boolean;
}

export interface TwoFactorState {
  enabled: boolean;
  telegram_connected: boolean;
}

export interface MyWeek {
  won: number;
  from: string;
  to: string;
}

interface Results<T> {
  results: T[];
}

export const profileApi = {
  updateMe: (body: ProfileUpdate): Promise<unknown> =>
    apiRequest<unknown>("/api/auth/me/", { method: "PATCH", body }),

  uploadAvatar: (file: File): Promise<unknown> => {
    const form = new FormData();
    form.append("avatar", file);
    return apiRequest<unknown>("/api/auth/me/avatar/", { method: "POST", body: form });
  },

  removeAvatar: (): Promise<unknown> =>
    apiRequest<unknown>("/api/auth/me/avatar/", { method: "DELETE" }),

  preferences: (): Promise<Preferences> => apiRequest<Preferences>("/api/me/preferences/"),

  updatePreferences: (body: Partial<Preferences>): Promise<Preferences> =>
    apiRequest<Preferences>("/api/me/preferences/", { method: "PATCH", body }),

  availability: (): Promise<Availability> => apiRequest<Availability>("/api/me/availability/"),

  updateAvailability: (body: AvailabilityUpdate): Promise<Availability> =>
    apiRequest<Availability>("/api/me/availability/", { method: "PATCH", body }),

  leaves: async (): Promise<LeaveRow[]> =>
    (await apiRequest<Results<LeaveRow>>("/api/me/leaves/")).results,

  requestLeave: (body: LeaveCreate): Promise<LeaveRow> =>
    apiRequest<LeaveRow>("/api/me/leaves/", { method: "POST", body }),

  cancelLeave: (id: number): Promise<LeaveRow> =>
    apiRequest<LeaveRow>(`/api/me/leaves/${String(id)}/cancel/`, { method: "POST" }),

  colleagues: async (): Promise<Colleague[]> =>
    (await apiRequest<Results<Colleague>>("/api/me/colleagues/")).results,

  teamStatus: async (): Promise<TeamMember[]> =>
    (await apiRequest<Results<TeamMember>>("/api/team/status/")).results,

  sessions: async (): Promise<DeviceSession[]> =>
    (await apiRequest<Results<DeviceSession>>("/api/auth/sessions/")).results,

  revokeSession: (id: number): Promise<null> =>
    apiRequest<null>(`/api/auth/sessions/${String(id)}/`, { method: "DELETE" }),

  revokeOtherSessions: (): Promise<{ revoked: number }> =>
    apiRequest<{ revoked: number }>("/api/auth/sessions/revoke-others/", { method: "POST" }),

  twoFactor: (): Promise<TwoFactorState> => apiRequest<TwoFactorState>("/api/auth/2fa/"),

  setTwoFactor: (enabled: boolean): Promise<TwoFactorState> =>
    apiRequest<TwoFactorState>("/api/auth/2fa/", { method: "PATCH", body: { enabled } }),

  myWeek: (): Promise<MyWeek> => apiRequest<MyWeek>("/api/deals/my-week/"),
};
